import { apiClient } from './client';

// Activity Log Types
export interface ActivityLog {
    id: string;
    userId: string;
    eventType: string;
    category: string;
    action: string;
    metadata: Record<string, unknown>;
    ipAddress?: string;
    userAgent?: string;
    requestId?: string;
    status: 'success' | 'failure' | 'warning';
    resourceType?: string;
    resourceId?: string;
    createdAt: string;
    user?: {
        id: string;
        email: string;
        name?: string;
    };
}

export interface SystemLog {
    id: string;
    level: 'error' | 'warn' | 'info' | 'debug';
    service: string;
    message: string;
    context: Record<string, unknown>;
    errorCode?: string;
    errorStack?: string;
    requestId?: string;
    userId?: string;
    source?: string;
    createdAt: string;
}

export interface LogPagination {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
}

export interface ActivityLogResponse {
    success: boolean;
    data: {
        logs: ActivityLog[];
        pagination: LogPagination;
    };
}

export interface SystemLogResponse {
    success: boolean;
    data: {
        logs: SystemLog[];
        pagination: LogPagination;
    };
}

export interface SystemLogStatsResponse {
    success: boolean;
    data: {
        errors: {
            totalErrors: number;
            byErrorCode: Array<{ errorCode: string; count: number }>;
            byService: Record<string, number>;
            recentErrors: Array<{
                id: string;
                message: string;
                errorCode?: string;
                source?: string;
                createdAt: string;
            }>;
        };
        levelDistribution: Record<string, number>;
    };
}

export interface ActivityFilterOptions {
    limit?: number;
    offset?: number;
    userId?: string;
    category?: string;
    eventType?: string;
    status?: string;
    resourceType?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
    orderBy?: string;
    order?: 'asc' | 'desc';
}

export interface SystemLogFilterOptions {
    limit?: number;
    offset?: number;
    level?: string;
    service?: string;
    errorCode?: string;
    requestId?: string;
    userId?: string;
    source?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
    orderBy?: string;
    order?: 'asc' | 'desc';
}

class AdminLogsService {
    // ===========================================================================
    // Activity Logs
    // ===========================================================================

    /**
     * Get all activity logs with filtering
     */
    async getActivityLogs(options: ActivityFilterOptions = {}): Promise<ActivityLogResponse> {
        const params = new URLSearchParams();

        Object.entries(options).forEach(([key, value]) => {
            if (value !== undefined && value !== '') {
                params.append(key, String(value));
            }
        });

        const queryString = params.toString();
        const url = queryString ? `/logs/activity?${queryString}` : '/logs/activity';

        const response = await apiClient.get(url);
        return response.data;
    }

    /**
     * Get a specific activity log
     */
    async getActivityById(id: string): Promise<{ success: boolean; data: ActivityLog }> {
        const response = await apiClient.get(`/logs/activity/${id}`);
        return response.data;
    }

    /**
     * Get available activity categories
     */
    async getActivityCategories(): Promise<{ success: boolean; data: { categories: string[] } }> {
        const response = await apiClient.get('/logs/activity/categories');
        return response.data;
    }

    /**
     * Get available event types
     */
    async getActivityEventTypes(): Promise<{ success: boolean; data: { eventTypes: string[] } }> {
        const response = await apiClient.get('/logs/activity/event-types');
        return response.data;
    }

    /**
     * Cleanup old activity logs
     */
    async cleanupActivityLogs(retentionDays: number = 90): Promise<{ success: boolean; message: string; data: { deletedCount: number } }> {
        const response = await apiClient.post('/logs/activity/cleanup', { retentionDays });
        return response.data;
    }

    // ===========================================================================
    // System Logs
    // ===========================================================================

    /**
     * Get system logs with filtering
     */
    async getSystemLogs(options: SystemLogFilterOptions = {}): Promise<SystemLogResponse> {
        const params = new URLSearchParams();

        Object.entries(options).forEach(([key, value]) => {
            if (value !== undefined && value !== '') {
                params.append(key, String(value));
            }
        });

        const queryString = params.toString();
        const url = queryString ? `/logs/system?${queryString}` : '/logs/system';

        const response = await apiClient.get(url);
        return response.data;
    }

    /**
     * Get a specific system log
     */
    async getSystemLogById(id: string): Promise<{ success: boolean; data: SystemLog }> {
        const response = await apiClient.get(`/logs/system/${id}`);
        return response.data;
    }

    /**
     * Get system log statistics
     */
    async getSystemLogStats(options: { startDate?: string; endDate?: string; service?: string } = {}): Promise<SystemLogStatsResponse> {
        const params = new URLSearchParams();

        if (options.startDate) params.append('startDate', options.startDate);
        if (options.endDate) params.append('endDate', options.endDate);
        if (options.service) params.append('service', options.service);

        const queryString = params.toString();
        const url = queryString ? `/logs/system/stats?${queryString}` : '/logs/system/stats';

        const response = await apiClient.get(url);
        return response.data;
    }

    /**
     * Get all services that have logged
     */
    async getSystemServices(): Promise<{ success: boolean; data: { services: string[] } }> {
        const response = await apiClient.get('/logs/system/services');
        return response.data;
    }

    /**
     * Trace all logs for a specific request
     */
    async traceRequest(requestId: string): Promise<{ success: boolean; data: { logs: SystemLog[] } }> {
        const response = await apiClient.get(`/logs/system/trace/${requestId}`);
        return response.data;
    }

    /**
     * Cleanup old system logs
     */
    async cleanupSystemLogs(retentionDays: number = 30): Promise<{ success: boolean; message: string; data: { deletedCount: number } }> {
        const response = await apiClient.post('/logs/system/cleanup', { retentionDays });
        return response.data;
    }
}

export const adminLogsService = new AdminLogsService();
